import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

export const AIChatWidget = () => {
  const { token, api } = useAuth();
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => `sess-${Math.random().toString(36).slice(2)}`);
  const scrollerRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, open, streaming]);

  const streamSend = async (q) => {
    setStreaming(true);
    setMessages(m => [...m, { role: "assistant", text: "" }]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${api}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: q, session_id: sessionId }),
        signal: controller.signal,
      });

      if (!resp.ok || !resp.body) throw new Error("stream failed");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          // A single SSE frame may span multiple `data:` lines — join them with a newline.
          const parts = frame.split("\n")
            .filter(l => l.startsWith("data:"))
            .map(l => l.slice(5).replace(/^ /, ""));
          if (parts.length === 0) continue;
          const payload = parts.join("\n");
          if (payload === "[DONE]") { setStreaming(false); return; }
          if (payload.startsWith("[ERROR]")) { throw new Error(payload); }
          acc += payload;
          setMessages(m => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", text: acc };
            return copy;
          });
        }
      }
    } catch (e) {
      // Fallback to sync
      try {
        const { data } = await axios.post(`${api}/ai/chat-sync`,
          { message: q, session_id: sessionId },
          { headers: { Authorization: `Bearer ${token}` } });
        setMessages(m => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: data.reply };
          return copy;
        });
      } catch {
        setMessages(m => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: "Athena is temporarily unavailable." };
          return copy;
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const send = () => {
    if (!input.trim() || streaming) return;
    if (!token) {
      setMessages(m => [...m, { role: "assistant", text: t("ai.requiresAuth") }]);
      return;
    }
    const q = input.trim();
    setInput("");
    setMessages(m => [...m, { role: "user", text: q }]);
    streamSend(q);
  };

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen(v => !v)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-foreground text-background flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
      >
        {open ? <X className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="ai-chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-6 z-40 w-[92vw] sm:w-[400px] h-[560px] max-h-[80vh] bg-background border border-border shadow-2xl flex flex-col"
          >
            <div className="border-b border-border p-4">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4" />
                <p className="font-display text-[15px] tracking-tight font-medium">{t("ai.title")}</p>
              </div>
              <p className="text-[11px] text-muted-foreground tracking-wide">{t("ai.subtitle")}</p>
            </div>

            <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && (
                <div className="text-[13px] text-muted-foreground leading-relaxed">
                  <p>{t("ai.subtitle")}</p>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-foreground text-background"
                      : "bg-secondary text-foreground border border-border"
                  }`}>
                    {m.text}
                    {streaming && i === messages.length - 1 && m.role === "assistant" && <span className="inline-block w-1.5 h-3 ml-1 bg-foreground/60 animate-pulse" />}
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-border p-3 flex items-center gap-2">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && send()}
                placeholder={t("ai.placeholder")}
                className="flex-1 bg-transparent text-[13px] px-2 py-2 outline-none placeholder:text-muted-foreground"
              />
              <button
                data-testid="ai-chat-send"
                onClick={send}
                disabled={streaming}
                className="w-9 h-9 flex items-center justify-center bg-foreground text-background disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] tracking-widest uppercase text-muted-foreground text-center pb-2">{t("ai.hint")}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
